import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import { API_ENDPOINT } from "../../config";
import { Student, StudentAddress, StudentBatch, UpdateStudentBatch } from "../../interfaces";
import { GetListDto } from "../../interfaces/dto/default.dto";
import { toastMessage } from "../../utils/toastError";

export type StudentAddressPayload = {
  label?: string;
  address_line_1: string;
  address_line_2?: string | null;
  landmark?: string | null;
  city?: string | null;
  post_office: string;
  district?: string | null;
  state: string;
  postal_code: string;
  delivery_instructions?: string | null;
  is_default?: boolean;
};

export type StudentAddressListResponse = {
  contact: {
    name: string | null;
    email: string;
    mobile_number: string;
    alternate_number: string | null;
  };
  addresses: StudentAddress[];
  total: number;
  max_active_addresses: number;
};

export const studentApi = createApi({
  reducerPath: "studentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/admin/student",
    credentials: "include",
  }),
  tagTypes: ["Student", "StudentAddress"],
  endpoints: (build) => ({
    getStudents: build.query<{ students: Student[]; total: number }, Partial<GetListDto>>({
      query: (params) => ({
        url: "/all",
        method: "GET",
        params,
      }),
      providesTags: ["Student"],
    }),
    getStudent: build.query<Student, string>({
      query: (id) => ({
        url: "/" + id,
        method: "GET",
      }),
    }),
    updateStudent: build.mutation<any, Partial<Student>>({
      query: ({ id, ...body }) => ({
        url: "/" + id,
        method: "PUT",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse() {
        toastMessage("success", "Succesfully updated");
      },
    }),
    getStudentAddresses: build.query<StudentAddressListResponse, string>({
      query: (studentId) => ({
        url: `/${studentId}/address`,
        method: "GET",
      }),
      providesTags: (_result, _error, studentId) => [{ type: "StudentAddress", id: studentId }],
    }),
    createStudentAddress: build.mutation<
      { address: StudentAddress },
      { studentId: string; body: StudentAddressPayload }
    >({
      query: ({ studentId, body }) => ({
        url: `/${studentId}/address`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { studentId }) => [
        { type: "StudentAddress", id: studentId },
        "Student",
      ],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(response: { address: StudentAddress }) {
        toastMessage("success", "Address added");
        return response;
      },
    }),
    updateStudentAddress: build.mutation<
      { address: StudentAddress },
      { studentId: string; addressId: string; body: Partial<StudentAddressPayload> }
    >({
      query: ({ studentId, addressId, body }) => ({
        url: `/${studentId}/address/${addressId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { studentId }) => [
        { type: "StudentAddress", id: studentId },
        "Student",
      ],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(response: { address: StudentAddress }) {
        toastMessage("success", "Address updated");
        return response;
      },
    }),
    setDefaultStudentAddress: build.mutation<
      { address: StudentAddress },
      { studentId: string; addressId: string }
    >({
      query: ({ studentId, addressId }) => ({
        url: `/${studentId}/address/${addressId}/default`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { studentId }) => [
        { type: "StudentAddress", id: studentId },
        "Student",
      ],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(response: { address: StudentAddress }) {
        toastMessage("success", "Default address updated");
        return response;
      },
    }),
    deleteStudentAddress: build.mutation<
      { address: StudentAddress; default_address: StudentAddress | null },
      { studentId: string; addressId: string }
    >({
      query: ({ studentId, addressId }) => ({
        url: `/${studentId}/address/${addressId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { studentId }) => [
        { type: "StudentAddress", id: studentId },
        "Student",
      ],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(response: { address: StudentAddress; default_address: StudentAddress | null }) {
        toastMessage("success", "Address deleted");
        return response;
      },
    }),
    updateStudentBatch: build.mutation<StudentBatch, UpdateStudentBatch>({
      query: (body) => ({
        url: "/batch",
        method: "PUT",
        body,
      }),
    }),
    deleteStudentBatch: build.mutation<any, string>({
      query: (id) => ({
        url: "/batch/" + id,
        method: "DELETE",
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
      transformResponse(response) {
        toastMessage("success", "Succesfully deleted");
        return response;
      },
    }),
    addStudentBatch: build.mutation<StudentBatch, UpdateStudentBatch>({
      query: (body) => ({
        url: "/batch",
        method: "POST",
        body,
      }),
    }),
    updateVerification: build.mutation<any, { student_id: string; verified: boolean }>({
      query: (body) => ({
        url: "/verification",
        method: "PUT",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(response: any) {
        toastMessage("success", response.message || "Verification updated successfully");
        return response;
      },
    }),
    assignTags: build.mutation<any, { student_id: string; tag_ids: string[] }>({
      query: (body) => ({
        url: "/assign-tags",
        method: "PUT",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue: any) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
      transformResponse(response: any) {
        toastMessage("success", response.message || "Tags assigned successfully");
        return response;
      },
    }),
  }),
});

export const {
  useGetStudentQuery,
  useLazyGetStudentQuery,
  useGetStudentsQuery,
  useLazyGetStudentsQuery,
  useUpdateStudentBatchMutation,
  useDeleteStudentBatchMutation,
  useAddStudentBatchMutation,
  useUpdateStudentMutation,
  useUpdateVerificationMutation,
  useAssignTagsMutation,
  useGetStudentAddressesQuery,
  useCreateStudentAddressMutation,
  useUpdateStudentAddressMutation,
  useSetDefaultStudentAddressMutation,
  useDeleteStudentAddressMutation,
} = studentApi;
